import { useState, useEffect } from 'react';
import { CheckCircle, FileText, Award, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { pageContentService } from '@/services/storage';
import type { PageContent } from '@/types';

const qualityItems = [
  {
    icon: <Shield className="h-8 w-8" />,
    title: '严格的质量控制',
    description: '从设计到生产的全流程质量管控',
  },
  {
    icon: <Award className="h-8 w-8" />,
    title: '国际认证',
    description: '产品通过USB-IF等国际权威认证',
  },
  {
    icon: <CheckCircle className="h-8 w-8" />,
    title: '可靠性测试',
    description: '全面的可靠性测试确保产品品质',
  },
  {
    icon: <FileText className="h-8 w-8" />,
    title: '完善的文档',
    description: '提供详细的技术规格书和应用笔记',
  },
];

export default function Technology() {
  const [pageContent, setPageContent] = useState<PageContent>({} as PageContent);

  useEffect(() => {
    pageContentService.get().then((content) => {
      setPageContent(content);
    });
  }, []);

  const qualitySystem = (pageContent.qualitySystem || {}) as { isoImage?: string };

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              技术与支持
            </h1>
            <p className="text-xl text-blue-100">
              完善的技术支持体系，助力您的产品开发
            </p>
          </div>
        </div>
      </section>

      {/* Quality System */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">质量体系</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              我们建立了完善的质量管理体系，确保每一颗芯片都符合最高品质标准
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityItems.map((item, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ISO Certification */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                ISO 9001 质量管理体系认证
              </h2>
              <p className="text-gray-600 mb-4">
                公司已通过ISO9001质量管理体系认证，建立了完善的质量管理体系，从设计、生产到售后服务的全过程进行严格的质量控制。
              </p>
              <ul className="space-y-3">
                {[
                  '严格的设计评审流程',
                  '完善的供应商管理',
                  '全面的生产测试',
                  '持续的质量改进',
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-video bg-gray-200 rounded-xl overflow-hidden">
              <img
                src={qualitySystem.isoImage || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80'}
                alt="ISO 9001 Certification"
                className="w-full h-full object-contain bg-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sample Apply CTA */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="bg-blue-600 text-white">
            <CardContent className="p-12">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">样品申请与购买</h2>
                  <p className="text-blue-100">
                    收到您的预约咨询后将会与您进行深度需求沟通。只需提供一些细节，我们就能回复您的询问。
                  </p>
                </div>
                <div className="flex justify-start lg:justify-end">
                  <Button
                    asChild
                    variant="secondary"
                    size="lg"
                    className="bg-white text-blue-600 hover:bg-gray-100"
                  >
                    <Link to="/sample-apply">立即申请</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
